// Side panel (right): opened by clicking a selected interactive object; closes with its X button, Esc, or a
// click on empty space in the view. First content: the room controls (RoomVars, contract section 7).
// The markup is in index.html (#panel). Inputs carry data-var="<RoomVars key>".
// Range inputs are goo sliders (port of the bike project's, see style.css): the native input stays the control.
// Each colour field has an editable hex value beside it (input.hex-field[data-hex-for="<key>"], ui/hex-field.ts):
// a valid value sets the colour field and goes out like a colour field change.

import type { RoomVarKey, RoomVars, RoomVarsPatch } from '@lr3d/shared';
import { bindHexField } from './hex-field';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

export class Panel {
  private el = $('panel');
  private inputs: HTMLInputElement[];
  private outputs = new Map<string, HTMLOutputElement>();
  private hexes = new Map<string, { show(hex: string): void }>();
  isOpen = false;
  /** A control changed (user input). */
  onInput: (patch: RoomVarsPatch) => void = () => {};
  onReset: () => void = () => {};
  /** The panel closed (focus goes back to the view). */
  onClose: () => void = () => {};

  constructor() {
    // index.html ships the panel `hidden` (display: none even before the stylesheet applies); closed it is
    // visibility: hidden and off screen (style.css .panel)
    this.el.hidden = false;
    this.inputs = Array.from(this.el.querySelectorAll<HTMLInputElement>('input[data-var]'));
    for (const o of Array.from(this.el.querySelectorAll<HTMLOutputElement>('output[data-for-var]'))) this.outputs.set(o.dataset.forVar!, o);
    for (const input of this.inputs) {
      const key = input.dataset.var as RoomVarKey;
      input.addEventListener('input', () => {
        const value = input.type === 'range' ? Number(input.value) : input.value.toLowerCase();
        this.showValue(key, value);
        this.placeHandle(input);
        this.onInput({ [key]: value } as RoomVarsPatch);
      });
    }
    for (const input of this.inputs) this.placeHandle(input);
    for (const field of Array.from(this.el.querySelectorAll<HTMLInputElement>('input.hex-field[data-hex-for]'))) {
      const key = field.dataset.hexFor as RoomVarKey;
      const color = this.inputs.find((i) => i.dataset.var === key);
      if (!color) continue;
      this.hexes.set(key, bindHexField(field, {
        current: () => color.value.toLowerCase(),
        apply: (hex) => {
          color.value = hex;
          this.onInput({ [key]: hex } as RoomVarsPatch);
        },
      }));
    }
    $('panel-close').addEventListener('click', () => this.close());
    $('panel-reset').addEventListener('click', () => this.onReset());
    // clicks inside the panel must not reach the view
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
  }

  /**
   * The room's controls (rooms.json `controls`): the heading, and only the controls of the listed room variables
   * (a control row holds an input[data-var]); a group with no shown control is hidden too. The concrete room has no
   * sun and no centre panel.
   */
  setRoom(controls: { title: string; vars: readonly string[] }): void {
    $('panel-title').textContent = controls.title;
    const show = new Set(controls.vars);
    for (const input of this.inputs) {
      const row = input.closest<HTMLElement>('.ctl');
      if (row) row.hidden = !show.has(input.dataset.var!);
    }
    for (const group of Array.from(this.el.querySelectorAll<HTMLElement>('.panel-group'))) {
      group.hidden = !Array.from(group.querySelectorAll<HTMLElement>('.ctl')).some((r) => !r.hidden);
    }
  }

  /** The heading is the room's (setRoom), whatever object opened the panel. */
  open(_object?: string): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.el.inert = false;
    this.el.removeAttribute('aria-hidden');
    this.el.classList.add('open');
    // focus the panel itself (not a slider: arrow keys keep walking until the user tabs into a control)
    this.el.focus({ preventScroll: true });
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.el.classList.remove('open');
    this.el.setAttribute('aria-hidden', 'true');
    if (this.el.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    this.el.inert = true;
    this.onClose();
  }

  /** Show the current values (incoming changes; a control being dragged gets the same value back). */
  set(vars: RoomVars): void {
    for (const input of this.inputs) {
      const key = input.dataset.var as RoomVarKey;
      const v = vars[key];
      const s = typeof v === 'number' ? String(v) : v;
      if (input.value !== s && !(input.type === 'range' && Number(input.value) === v)) {
        input.value = s;
        this.placeHandle(input);
      }
      this.showValue(key, v);
    }
  }

  /** Goo slider: the visible handle follows the input through --p (0..1); CSS turns it into a transform. */
  private placeHandle(input: HTMLInputElement): void {
    if (input.type !== 'range') return;
    const min = Number(input.min), max = Number(input.max);
    const p = max > min ? (Number(input.value) - min) / (max - min) : 0;
    (input.parentElement as HTMLElement).style.setProperty('--p', p.toFixed(4));
  }

  private showValue(key: string, v: number | string): void {
    if (typeof v === 'string') this.hexes.get(key)?.show(v);
    const o = this.outputs.get(key);
    if (o) o.textContent = typeof v === 'number' ? v.toFixed(2) : v;
  }
}
